import { Fragment, useRef, useState } from 'react';
import { Link, useNotify, useRecordContext, type RaRecord } from 'react-admin';
import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import axiosNest from '../../api/axiosNest';
import { SectionCard } from '../../shared/Panel';
import { addCandidate, toErrorMessage } from '../userOffers/orderActionsApi';
import { DeliveryCell, type DeliveryEstimate } from '../userOffers/DeliveryCell';
import { WarningsList } from '../userOffers/WarningsList';
import { BasisList, BasisToggle, type SuggestionBasisItem } from '../userOffers/SuggestionBasis';
import { FreeFromCell, ReserveColumnHeader, WorkReserveCell, WorkReserveHeader } from '../userOffers/suggestionColumns';
import { fullDateTime, shortDateTime } from '../../shared/dateTime';
import { plural } from '../../shared/plural';
import { recordScoped, useStillMounted } from '../../shared/recordScoped';
import { TABLE_SCROLL_SX } from '../../shared/tableScroll';
import { isOrderVehicle, vehicleTypeLabel } from './vehicleTypes';
import { AccessCostCell } from '../userOffers/AccessCostCell';
import { MarginCell } from '../userOffers/MarginCell';
import { ReleaseVariantButton } from '../userOffers/ReleaseVariantButton';
import type { AccessCost, CandidateMargin, WorkReserve } from '../userOffers/suggestionsApi';
import { AccessDistanceCell, OrderRouteNote } from '../userOffers/RouteFigureCell';
import { accessSourceNote, isMeasured, type RouteFigure } from '../userOffers/routeFigure';
import { useHasPermission } from '../PermissionGate';
import { useVariantScope, withVariantScopeNote } from '../userOffers/variantScope';
import { variantRefusalOf, windowWarningOf, withWindowWarning } from '../userOffers/variantWindow';
import { VARIANT_TOOLTIP } from '../userOffers/VehicleSuggestions';
import { ORDER_SUGGESTIONS_ANCHOR_ID, useCarCard } from './CarCardContext';
import { byGrade, suggestionGrade, type SuggestionGrade } from './suggestionGrade';
import { useFeature } from '../featureFlags/FeatureGate';
import { byPairScore, PairScoreLine, PairScoreSortToggle } from '../userOffers/PairScore';
import { VEHICLE_PREVIEW_SEGMENTS } from './fleetFeatures';
import { fetchVehiclePreview, type PlanItem } from './vehiclePlanApi';
import type { GanttPreview } from './VehicleGantt';
import { SuggestionsAxis } from './SuggestionsAxis';
import type {
  AfterOrder,
  Feasibility,
  PairScore,
  WindowReserve,
} from '../userOffers/suggestionsApi';
import {
  AfterOrderLine,
  byFeasibility,
  ComputedAtNote,
  FEATURE_COLUMN_SORT,
  FEATURE_DETAILS,
  FEATURE_FEASIBILITY,
  FeasibilityChip,
  nextSort,
  SkippedReasonList,
  SortableLabel,
  sortRecommendations,
  WindowReserveCell,
  type RecommendationSort,
  type RecommendationSortKey,
} from '../userOffers/recommendationView';

/** Liczba z jednostką i data nie łamią się — „+100,8 / h” czyta się jak dwie wartości. */
const NOWRAP = { whiteSpace: 'nowrap' } as const;

/**
 * Szerokości, które trzymają tabelę „Rekomendowane zlecenia dla pojazdu” w karcie przy 1366 px (02.11.2026):
 * smoke stagingu na kopii produkcji pokazał +28 px przewijania w bok, gdy zapas między szerokością
 * minimalną tabeli a kartą wynosił ok. 40 px. Uwagi łamią się na więcej linii, a linie wariantów
 * (autor, inne warianty) nie są szersze niż numer zlecenia — pełna treść w dymku.
 */
export const NOTES_MIN_WIDTH = 112;
export const VARIANT_LINE_MAX_WIDTH = 124;

/** Te same węższe marginesy i wyrównanie do góry co w podpowiedzi pojazdów. */
const TABLE_SX = {
  '& .MuiTableCell-root': { px: 1 },
  '& .MuiTableCell-body': { verticalAlign: 'top' },
  '& .MuiTableCell-head': { verticalAlign: 'bottom' },
} as const;

/** Kolor czipu „Ocena” — zielony, pomarańczowy (ostrzeżenie), szary, czerwony. */
const GRADE_COLOR: Record<SuggestionGrade, 'success' | 'warning' | 'default' | 'error'> = {
  ok: 'success',
  caution: 'warning',
  unknown: 'default',
  late: 'error',
};

/** Ta sama pozycja startu dolotu — wtedy „Wolny od” wystarczy raz, nad tabelą. */
function samePosition(a: SuggestionPosition | null | undefined, b: SuggestionPosition | null | undefined): boolean {
  if (!a || !b) return !a && !b;
  return a.freeFrom === b.freeFrom && a.zrodlo === b.zrodlo && (a.fromOrder ?? null) === (b.fromOrder ?? null);
}

/**
 * „Wolny od” nad tabelą (prototyp, zmiana 15): od kiedy i skąd pojazd rusza
 * po bieżącym kursie — od tego miejsca liczymy dolot.
 */
const FreeFromSummary = ({
  position,
  perRow,
  showPlace = false,
}: {
  position: SuggestionPosition | null;
  perRow: boolean;
  /** Miasto rozładunku przy pozycji z historii (`recommendationDetails`). */
  showPlace?: boolean;
}) => (
  <Typography
    variant="body2"
    data-testid="free-from-summary"
    sx={{ mb: 1, px: 1.5, py: 1, bgcolor: 'action.hover', borderRadius: 1 }}
  >
    {position ? (
      <>
        Pojazd wolny od <strong>{shortDateTime(position.freeFrom)}</strong>
        {position.fromOrder ? (
          <>
            , po zleceniu{' '}
            {position.fromOrderId ? (
              <Link to={`/user-offers/${position.fromOrderId}/show`}>{position.fromOrder}</Link>
            ) : (
              position.fromOrder
            )}
          </>
        ) : position.zrodlo === 'TELEMATYKA' && position.measuredAt ? (
          `, z ostatniej pozycji GPS (odczyt ${shortDateTime(position.measuredAt)})`
        ) : null}
        {/* Miasto rozładunku przy pozycji z historii (15.10.2026). */}
        {showPlace && position.fromOrder && position.adres?.city
          ? ` (rozładunek: ${[position.adres.country, position.adres.city].filter(Boolean).join(' ')})`
          : null}
        {' — od tego miejsca liczymy dolot do każdego zlecenia. '}
      </>
    ) : (
      'Nie wiemy, od kiedy pojazd jest wolny. '
    )}
    {perRow
      ? 'Gdy przed zleceniem pojazd ma inne obsadzenie, dolot liczymy od jego rozładunku — pokazuje to kolumna „Wolny od”. '
      : null}
    Najedź na wiersz, aby zobaczyć zlecenie na harmonogramie.
  </Typography>
);

/**
 * Kolumna akcji jak w rekomendacjach pojazdów (`VehicleSuggestions`):
 * „Wariant” (przycisk albo znacznik), pod nim ikony z podpisem w dymku —
 * w pionie, wyrównane do prawej, napisy się nie łamią (06.10.2026). Dotąd
 * znacznik „wariant już dodany” i przycisk „Usuń wariant” słowami dawały
 * kolumnę ok. 145 px, a tabela przy oknie 1366 px przewijała się w bok
 * i ucinała przyciski („USU”, „DOD”).
 */
const ACTIONS_COLUMN_SX = {
  alignItems: 'flex-end',
  '& > *': { flexShrink: 0 },
} as const;
const ACTIONS_ICONS_SX = { justifyContent: 'flex-end' } as const;

/** Dymek przy znaczniku „Wariant”. */
export const ALREADY_VARIANT_TOOLTIP = 'Pojazd jest już wariantem tego zlecenia.';

/**
 * Miejsce relacji: kraj z pierwszym słowem nazwy trzyma się razem („DE
 * ZEHDENICK”), a dłuższa nazwa łamie się po słowach — w jednej linii
 * „PL GORZÓW WIELKOPOLSKI” poszerzało kolumnę „Relacja” (06.10.2026).
 */
const placeText = (place: string) => place.replace(' ', '\u00a0');

/**
 * Skąd liczony dolot do zlecenia — GPS z czasem odczytu albo rozładunek
 * obsadzenia. Pozycja zależy od zlecenia (28.09.2026): po przyszłym
 * obsadzeniu auto rusza z jego rozładunku, przed nim — z GPS. Na ekranie
 * w kolumnie „Wolny od” (`suggestionColumns.tsx`, 01.10.2026).
 */
export interface SuggestionPosition {
  zrodlo: string;
  fromOrder: string | null;
  /** Id zlecenia `fromOrder` — link do jego karty. Brak = starsze API. */
  fromOrderId?: number | null;
  measuredAt: string | null;
  freeFrom: string;
  /** Adres pozycji — przy historii rozładunek (miasto od 15.10.2026). */
  adres?: { country: string | null; postalCode?: string | null; city: string | null } | null;
}

/**
 * Warianty zlecenia z autorem (15.10.2026, przełącznik `recommendationDetails`):
 * wariant tego auta i warianty innych aut. Brak = wyłączony.
 */
export interface OrderVariants {
  own: { addedBy: string | null; addedAt: string | null; origin: string | null } | null;
  others: Array<{ carId: number; registrationNumber: string | null; addedBy: string | null }>;
}

export interface OrderSuggestion {
  userOfferId: number;
  externalId: string | null;
  planningStatus: string;
  from: string;
  to: string;
  fromDate: string;
  accessDistance: { roadKm: number; straightKm: number; multiplier: number };
  /**
   * Dolot z podpisanym źródłem — trasa z map HERE albo szacunek, z rachunkiem
   * poglądowym (06.10.2026). Brak = starsze API, zostaje `accessDistance`.
   */
  accessRoute?: RouteFigure;
  /** Trasa zlecenia z podpisanym źródłem; `null` = nie liczymy. Brak = starsze API. */
  route?: RouteFigure | null;
  /** Pozycja, od której liczony dolot tego zlecenia; starsza odpowiedź API może jej nie mieć. */
  position?: SuggestionPosition;
  spareHours: number;
  onTime: { willMakeIt: boolean | null; reason: string | null; certain: boolean };
  /** Szacowany przyjazd na rozładunek; starsza odpowiedź API może go nie mieć. */
  delivery?: DeliveryEstimate;
  /**
   * Zapas czasu pracy kierowcy po wykonaniu zlecenia (06.10.2026) — ta sama
   * reguła co przy podpowiedzi pojazdów, więc ta sama para auto–zlecenie ma
   * w obu kierunkach ten sam zapas. Brak = starsze API.
   */
  workReserve?: WorkReserve;
  ostrzezenia: string[];
  alreadyCandidate: boolean;
  /**
   * Koszt dolotu w PLN (omówienie 01.10.2026: „ilość kilometrów, ilość
   * złotówek”) — ta sama reguła co przy podpowiedzi pojazdów, stawki spółki
   * pojazdu, kurs NBP z dnia załadunku. Brak = starsze API.
   */
  accessCost?: AccessCost;
  /**
   * Marża z pasmem alertu — ta sama reguła co przy podpowiedzi pojazdów:
   * cena zlecenia w PLN (kurs NBP z dnia załadunku) minus koszt dolotu
   * i trasy. Alert, nie ranking. Brak = starsze API.
   */
  margin?: CandidateMargin;
  /**
   * Na jakiej podstawie — te same punkty co przy podpowiedzi pojazdów dla tej
   * samej pary auto–zlecenie (`suggestion-basis.ts`). Brak = starsze API.
   */
  basis?: SuggestionBasisItem[];
  /**
   * Ocena pary 0–10 (13.10.2026) — ta sama reguła i to samo wejście co przy
   * rekomendacji pojazdów. Brak = przełącznik `pairScoring` wyłączony.
   */
  pairScore?: PairScore;
  /** Wykonalność (15.10.2026) — z API; brak = przełącznik wyłączony. */
  feasibility?: Feasibility;
  /** Zapas do okna załadunku (15.10.2026). */
  windowReserve?: WindowReserve;
  /** Po zleceniu: gdzie i kiedy wolny (15.10.2026). */
  afterOrder?: AfterOrder;
  /** Warianty zlecenia z autorem (15.10.2026). */
  variants?: OrderVariants;
  /**
   * Realizacja pewnie poza oknami zlecenia (21.10.2026, `variantWithinWindows`)
   * — „Wariant” wyszarzony z tym powodem. Brak = wolno.
   */
  variantRefusal?: string;
}

/** Nagłówek pierwszej kolumny z wykonalnością z API (15.10.2026), bez oceny pary. */
export const FIRST_COLUMN_HEADER_FEASIBILITY = 'Zlecenie i wykonalność';

/**
 * „wariant: anna.planista” i „inne warianty: WX12345 (jan.planista)” —
 * stan wariantu przy zleceniu (makieta FTL, Etap 1).
 */
export function otherVariantsText(v: OrderVariants): string | null {
  if (v.others.length === 0) return null;
  return `inne warianty: ${v.others
    .map((o) => `${o.registrationNumber ?? `#${o.carId}`}${o.addedBy ? ` (${o.addedBy})` : ''}`)
    .join(', ')}`;
}

/**
 * Nagłówek pierwszej kolumny. Z oceną pary (13.10.2026) czip z werdyktów
 * to „wykonalność”, a liczba 0–10 — „ocena”: dwie różne rzeczy, dwie nazwy.
 */
export const FIRST_COLUMN_HEADER = 'Zlecenie i ocena';
export const FIRST_COLUMN_HEADER_SCORED = 'Zlecenie, wykonalność i ocena';

export interface OrderSuggestionsResult {
  refusal: string | null;
  orders: OrderSuggestion[];
  skippedSummary: Record<string, number>;
  /** Podpisy powodów z `skippedSummary` po polsku, z API (07.10.2026); brak = starsze API. */
  skippedLabels?: Record<string, string>;
  /**
   * Pominięte zlecenia imiennie ze zdaniem powodu (15.10.2026,
   * `recommendationDetails`); brak = wyłączony albo starsze API.
   */
  skipped?: Array<{ userOfferId: number; externalId: string | null; reason: string; reasonText: string }>;
  /** Pozycja po bieżącym kursie — dolot każdego zlecenia liczy się od jego `position`. */
  position: SuggestionPosition | null;
  horizonDays: number;
}

export async function fetchOrderSuggestions(carId: number | string): Promise<OrderSuggestionsResult> {
  const { data } = await axiosNest.get(`/user-offers/for-car/${carId}/suggestions`);
  return data;
}

/** Tytuł sekcji (poprawa brzmienia 01.10.2026: rekomendacja, nie „podpowiedź”). */
const TITLE = 'Rekomendowane zlecenia dla pojazdu';

/**
 * „Nie pokazano 3 zleceń: kolizja z innym zleceniem tego pojazdu (2), …” albo
 * `null`. Powody z API (`ORDER_SKIP_REASON` w `order-suggestions.ts`) są
 * kluczami odpowiedzi; podpisy przychodzą z API obok nich (`skippedLabels`,
 * 07.10.2026: „mapa etykiet po stronie API”). Starsze API — sam klucz.
 */
function skippedOrdersText(
  summary: Record<string, number>,
  labels: Record<string, string> = {},
): string | null {
  const entries = Object.entries(summary);
  if (entries.length === 0) return null;
  const total = entries.reduce((sum, [, n]) => sum + n, 0);
  const reasons = entries.map(([reason, n]) => `${labels[reason] ?? reason} (${n})`).join(', ');
  return `Nie pokazano ${plural(total, 'zlecenia', 'zleceń', 'zleceń')}: ${reasons}.`;
}

/**
 * „3 zlecenia, w tym 1 bez zastrzeżeń” nad tabelą (prototyp, zmiana 15 i linia
 * stanu z wzorca 13.10) — „bez zastrzeżeń” to ocena „mieści się” z werdyktów.
 */
export function suggestionsCountText(orders: ReadonlyArray<Parameters<typeof suggestionGrade>[0]>): string {
  const ok = orders.filter((o) => suggestionGrade(o).grade === 'ok').length;
  return `${plural(orders.length, 'zlecenie', 'zlecenia', 'zleceń')}, w tym ${ok} bez zastrzeżeń`;
}

/**
 * Podgląd z odcinkami (15.10.2026, makieta FTL Etap 1): pozycja z API
 * (`GET /car-schedules/preview`) dokłada do podglądu odcinki szacunku
 * i spóźnienie po oknie. Bez odcinków (trasa nieznana) — podgląd zostaje
 * jednym paskiem.
 */
export function previewWithSegments(base: GanttPreview, item: PlanItem | null | undefined): GanttPreview {
  const segments = item?.segments ?? [];
  if (segments.length === 0) return base;
  return {
    ...base,
    segments,
    overrunUntil: item?.overrunUntil ?? null,
    windowTo: item?.windowTo ?? null,
    // Nawiasy Z/R podglądu (24.10.2026, S-24.10-05) — gdy API je podało.
    ...(item?.windowBrackets ? { windowBrackets: item.windowBrackets, orderId: item.orderId } : {}),
  };
}

/** „w ciągu 7 dni” — z odmianą, bo horyzont to parametr. */
const withinDays = (n: number) => `w ciągu ${plural(n, 'dnia', 'dni', 'dni')}`;

/** Skąd liczony dolot — jedno zdanie pod tabelą. */
function accessOriginText(result: OrderSuggestionsResult): string {
  if (result.orders.some((o) => o.position)) {
    return (
      'Liczymy go od miejsca, w którym pojazd będzie przed załadunkiem danego zlecenia, czyli po ' +
      'zakończeniu wcześniejszych obsadzeń, także zaplanowanych. Źródło pozycji podaje kolumna „Wolny od”.'
    );
  }
  if (result.position?.zrodlo === 'TELEMATYKA') return 'Liczymy go od ostatniej pozycji GPS.';
  if (result.position?.fromOrder) return `Liczymy go od rozładunku zlecenia ${result.position.fromOrder}.`;
  return 'Liczymy go od ostatniej znanej pozycji pojazdu.';
}

/**
 * Zlecenia dla tego pojazdu — podpowiedź w drugą stronę (FTL V2.1 §12:
 * „pojazd → zlecenia”, Etap 1: rekomendacje dwukierunkowe).
 *
 * Te same zasady co przy podpowiedzi pojazdów: kolejność po dolocie
 * (szacunek), składniki osobno, żadnej oceny złożonej. Odpadają wyłącznie
 * zlecenia kolidujące z obsadzeniami auta i z załadunkiem przed jego
 * zwolnieniem — i mówimy, ile ich było. „Dodaj wariant” zakłada kandydaturę
 * przy zleceniu; obsadzenie zostaje osobną decyzją przy zleceniu.
 *
 * Omówienie 01.10.2026 [1:40:10–1:42:38]: przy każdym zleceniu kilometry
 * i złotówki — koszt dolotu pod dolotem, jak w drugą stronę (składnik, nie
 * ranking: kolejność zostaje po dolocie) — oraz „Usuń wariant” przy
 * zleceniu, do którego auto już jest wariantem. Usunięcie to zwolnienie
 * kandydatury z powodem (`ReleaseVariantButton`, wpis w dzienniku
 * zlecenia); przycisk widzi tylko rola z `FTL_PLANNING`, a API i tak
 * odmawia bez niego (403).
 */
const CarOrderSuggestionsPanel = () => {
  const record = useRecordContext<RaRecord>();
  const notify = useNotify();
  const stillMounted = useStillMounted();
  const canPlan = useHasPermission('FTL_PLANNING');
  const { setPreview } = useCarCard();
  // Planista dodaje i usuwa warianty tylko przy swoich autach (07.10.2026,
  // `variantScope.ts`): na karcie cudzego auta nie ma „Wariant” ani
  // „Usuń wariant” — znacznik „Wariant” zostaje.
  const variantScope = useVariantScope();
  const [result, setResult] = useState<OrderSuggestionsResult | null>(null);
  const [busy, setBusy] = useState(false);
  // „Na jakiej podstawie” — rozwinięte uzasadnienie jednego zlecenia naraz.
  const [basisFor, setBasisFor] = useState<number | null>(null);
  // Ocena pary 0–10 (13.10.2026) — przełącznik funkcji; „Sortuj po ocenie”
  // to wybór widoku, domyślnie kolejność jak dotąd.
  const scoring = useFeature('pairScoring');
  const [sortByScore, setSortByScore] = useState(false);
  // Podgląd z odcinkami (makieta FTL Etap 1, 15.10.2026): odpowiedź API na
  // zlecenie trzymamy do przeliczenia listy (`null` — odpowiedzi nie ma albo
  // był błąd: zostaje podgląd jednym paskiem); `hovered` — wiersz pod myszą,
  // żeby spóźniona odpowiedź nie nadpisała podglądu innego zlecenia.
  const previewSegments = useFeature(VEHICLE_PREVIEW_SEGMENTS);
  const previewCache = useRef(new Map<number, PlanItem | null>());
  const hovered = useRef<number | null>(null);
  // Makieta FTL, Etap 1 (15.10.2026) — te same przełączniki i ta sama treść
  // co przy rekomendacji pojazdów (`recommendationView.tsx`).
  const feasibilityOn = useFeature(FEATURE_FEASIBILITY);
  const detailsOn = useFeature(FEATURE_DETAILS);
  const columnSortOn = useFeature(FEATURE_COLUMN_SORT);
  const [sort, setSort] = useState<RecommendationSort | null>(null);
  const [computedAt, setComputedAt] = useState<string | null>(null);
  const [showSkipped, setShowSkipped] = useState(false);
  if (!record) return null;
  const onSort = (column: RecommendationSortKey) => {
    setSort((s) => nextSort(s, column));
    setSortByScore(false);
  };
  const onScoreSort = (checked: boolean) => {
    setSortByScore(checked);
    if (checked) setSort(null);
  };
  const sortHandler = columnSortOn ? onSort : undefined;

  // Naczepa i wózek nie stają pod zlecenie — API odmawia im podpowiedzi
  // (404), więc zamiast przycisku, który kończy się błędem, mówimy, dla
  // kogo ta sekcja jest.
  if (!isOrderVehicle(record.carType)) {
    return (
      <SectionCard title={TITLE}>
        <Typography variant="body2" color="text.secondary">
          Rekomendacje zleceń dotyczą tylko pojazdów wykonujących przewozy, a nie naczep i wózków
          widłowych. Typ tego pojazdu: {vehicleTypeLabel(record.carType)}.
        </Typography>
      </SectionCard>
    );
  }

  // `readOnly`: błąd odczytu dla auta, z którego planista już przeszedł na
  // inne, milczy; błąd zapisu (wariant) mówimy zawsze — to jego akcja.
  const run = async (action: () => Promise<unknown>, readOnly = false) => {
    setBusy(true);
    try {
      await action();
    } catch (e) {
      if (!readOnly || stillMounted()) notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };
  // Nowa lista = nowe pozycje auta (np. po dodaniu wariantu) — podglądy od
  // nowa; wynik z godziną przeliczenia (15.10.2026).
  const fresh = async () => {
    previewCache.current.clear();
    const data = await fetchOrderSuggestions(record.id);
    setResult(data);
    setComputedAt(new Date().toISOString());
  };
  const load = () => void run(fresh, true);
  // Po usunięciu wariantu — świeża lista (zlecenie traci „wariant już dodany”).
  const reload = () => run(fresh, true);
  const hidePreview = () => {
    hovered.current = null;
    setPreview(null);
  };
  const vehicleName = String(record.registrationNumber ?? record.name ?? `#${record.id}`);
  const canManageVariant = variantScope(record.ftlCaretaker as string | null | undefined);
  const addVariant = (o: OrderSuggestion) =>
    void run(async () => {
      const added: unknown = await addCandidate(o.userOfferId, Number(record.id), { origin: 'order_suggestion' });
      // Ostrzeżenie o oknach (21.10.2026, `variantWithinWindows`) — wariant
      // jest, ale wynik niepewny albo brak danych.
      notify(
        withWindowWarning(
          withVariantScopeNote(
            `Pojazd został dodany jako wariant do zlecenia ${o.externalId ?? `#${o.userOfferId}`}.`,
            added,
          ),
          added,
        ),
        { type: windowWarningOf(added) ? 'warning' : 'success' },
      );
      await fresh();
    });

  const skipped = result ? skippedOrdersText(result.skippedSummary, result.skippedLabels) : null;
  // Choć jeden dolot z map: nagłówek bez „(szacunek)”, źródło w każdej
  // komórce — inaczej szacunek wyglądałby jak trasa z map.
  const measuredAccess = result?.orders.some((o) => isMeasured(o.accessRoute)) ?? false;
  const accessNote = result
    ? accessSourceNote(
        result.orders.map((o) => o.accessRoute),
        result.orders.map((o) => o.route),
      )
    : null;
  // Opłaty drogowe z HERE w koszcie (06.10.2026) — zdanie o koszcie mówi
  // o nich tylko wtedy, gdy API je podaje.
  const withTolls =
    result?.orders.some((o) => o.accessRoute?.source === 'here' && o.accessRoute.tolls !== undefined) ??
    false;

  // Wykonalne na górze (zmiana 15), w grupie — po dolocie, jak z API.
  // „Sortuj po ocenie” (13.10.2026): od najwyższej oceny pary, bez oceny
  // na końcu, przy równej — kolejność z API.
  // Sortowanie nagłówkiem (15.10.2026) — tylko widok; z wykonalnością z API
  // kolejność domyślna „wykonalne na górze” liczy się z niej, bez —
  // z werdyktów na ekranie, jak dotąd.
  const orders = result
    ? scoring && sortByScore
      ? byPairScore(result.orders)
      : columnSortOn && sort
        ? sortRecommendations(result.orders, sort)
        : feasibilityOn && result.orders.some((o) => o.feasibility)
          ? byFeasibility(result.orders)
          : byGrade(result.orders)
    : [];
  // „Wolny od” w wierszu tylko wtedy, gdy któreś zlecenie liczy dolot z innego
  // miejsca niż ogólne — inaczej wystarcza zdanie nad tabelą.
  const freeFromColumn = orders.some((o) => o.position && !samePosition(o.position, result?.position));
  const columns = freeFromColumn ? 9 : 8;

  return (
    // Karta MUI przycina treść (`overflow: hidden`) — przyklejony pasek osi
    // nad tabelą (`SuggestionsAxis`) potrzebuje `visible`.
    <SectionCard title={TITLE} sx={previewSegments ? { overflow: 'visible' } : undefined}>
      <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
        {/* `data-suggest-start` — cel przycisku z banera alertu (`showOrderSuggestions`). */}
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          onClick={load}
          data-suggest-start={result === null ? 'true' : undefined}
        >
          {result === null ? 'Pokaż rekomendowane zlecenia' : 'Przelicz ponownie'}
        </Button>
        {busy && <CircularProgress size={18} />}
        {scoring && result !== null && result.orders.length > 0 && (
          <PairScoreSortToggle checked={sortByScore} onChange={onScoreSort} />
        )}
        {/* Linia stanu (wzorzec 13.10, „N zleceń, w tym X bez zastrzeżeń”). */}
        {result !== null && !result.refusal && result.orders.length > 0 && (
          <Typography variant="body2" color="text.secondary" data-testid="suggestions-count">
            {suggestionsCountText(result.orders)}
          </Typography>
        )}
        {detailsOn && result !== null && <ComputedAtNote at={computedAt} />}
      </Stack>

      {result?.refusal && <Alert severity="info">{result.refusal}</Alert>}

      {result && !result.refusal && result.orders.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Wśród zleceń bez przypisanego pojazdu z załadunkiem {withinDays(result.horizonDays)} nie ma takich,
          które ten pojazd mógłby obsłużyć.
        </Typography>
      )}

      {result && result.orders.length > 0 && (
        <FreeFromSummary position={result.position} perRow={freeFromColumn} showPlace={detailsOn} />
      )}

      {/* Oś przyklejona nad tabelą (15.10.2026, przegląd Etapu 1 przy
          1366×768): podgląd zlecenia z wiersza widać bez przewijania do
          harmonogramu (`SuggestionsAxis`). */}
      {previewSegments && result && result.orders.length > 0 && <SuggestionsAxis horizonDays={result.horizonDays} />}

      {result && result.orders.length > 0 && (
        <Box sx={TABLE_SCROLL_SX} data-testid="order-suggestions-scroll">
          <Table size="small" sx={TABLE_SX}>
            <TableHead>
              <TableRow>
                {/* Ocena pod numerem, nie osobną kolumną: przy 1366 px osobna kolumna
                    przewijała tabelę w bok o 36 px (e2e 07.10.2026). */}
                <TableCell>
                  <SortableLabel sortKey="feasibility" sort={sort} onSort={feasibilityOn ? sortHandler : undefined}>
                    {scoring
                      ? FIRST_COLUMN_HEADER_SCORED
                      : feasibilityOn
                        ? FIRST_COLUMN_HEADER_FEASIBILITY
                        : FIRST_COLUMN_HEADER}
                  </SortableLabel>
                </TableCell>
                <TableCell>Relacja</TableCell>
                <TableCell>Załadunek</TableCell>
                {/* Nagłówki opisowe łamią się po słowach — w jednej linii
                    „Dolot i koszt (szacunek)” i „Rozładunek (szacunek)” były
                    szersze niż liczby pod nimi (06.10.2026). */}
                <TableCell align="right">
                  <SortableLabel sortKey="access" sort={sort} onSort={sortHandler}>
                    {measuredAccess ? 'Dolot i koszt' : 'Dolot i koszt (szacunek)'}
                  </SortableLabel>
                  {/* Marża stoi pod kosztem — sortowanie po niej z podpisu. */}
                  {sortHandler && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      <SortableLabel sortKey="margin" sort={sort} onSort={sortHandler}>
                        marża
                      </SortableLabel>
                    </Typography>
                  )}
                </TableCell>
                {freeFromColumn ? (
                  <TableCell sx={NOWRAP}>
                    <SortableLabel sortKey="freeFrom" sort={sort} onSort={sortHandler}>
                      Wolny od
                    </SortableLabel>
                  </TableCell>
                ) : null}
                {/* „Zapas do okna” nad „Zapasem czasu pracy” w jednej kolumnie
                    (integracja Etapu 1, 15.10.2026) — osobna kolumna nie
                    mieściła się przy 1366 px; jak w rekomendacjach pojazdów. */}
                {feasibilityOn ? (
                  <TableCell align="right">
                    <ReserveColumnHeader sort={sort} onSort={sortHandler} />
                  </TableCell>
                ) : (
                  <TableCell align="right">
                    <SortableLabel sortKey="workReserve" sort={sort} onSort={sortHandler}>
                      <WorkReserveHeader />
                    </SortableLabel>
                  </TableCell>
                )}
                <TableCell>
                  <SortableLabel sortKey="delivery" sort={sort} onSort={sortHandler}>
                    Rozładunek (szacunek)
                  </SortableLabel>
                </TableCell>
                {/* 140 px: przy 160 tabela nie mieściła się w karcie przy oknie
                    1366 px (06.10.2026) — uwagi łamią się na więcej linii.
                    112 px od 02.11.2026: na kopii produkcji przy 1366 px tabela znów
                    przewijała się w bok (+28 px, smoke stagingu) — zapas na dłuższe dane. */}
                <TableCell sx={{ minWidth: NOTES_MIN_WIDTH }}>Uwagi</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody onMouseLeave={hidePreview} onBlur={hidePreview}>
              {orders.map((o) => {
                const graded = suggestionGrade(o);
                // Wykonalność z API (15.10.2026) zastępuje ocenę z ekranu.
                const feasibility = feasibilityOn ? o.feasibility : undefined;
                const showOnPlan = () => {
                  const id = o.userOfferId;
                  const base: GanttPreview = {
                    label: o.externalId ?? `#${id}`,
                    from: o.fromDate,
                    to: o.delivery?.arrivalAt ?? null,
                    late: feasibility ? feasibility.grade === 'late' : graded.grade === 'late',
                  };
                  hovered.current = id;
                  const cached = previewCache.current.get(id);
                  setPreview(previewWithSegments(base, cached));
                  if (!previewSegments || previewCache.current.has(id)) return;
                  // Zapamiętujemy od razu — drugie najechanie nie pyta drugi raz.
                  previewCache.current.set(id, null);
                  fetchVehiclePreview(record.id, id)
                    .then((p) => {
                      const item = p?.item ?? null;
                      previewCache.current.set(id, item);
                      if (hovered.current === id) setPreview(previewWithSegments(base, item));
                    })
                    .catch(() => {
                      // Bez odcinków podgląd zostaje jednym paskiem — nic nie zgadujemy.
                    });
                };
                const otherVariants = detailsOn && o.variants ? otherVariantsText(o.variants) : null;
                return (
                <Fragment key={o.userOfferId}>
                  <TableRow
                    hover
                    data-testid="order-suggestion-row"
                    onMouseEnter={showOnPlan}
                    onFocus={showOnPlan}
                  >
                    <TableCell sx={NOWRAP}>
                      <Link to={`/user-offers/${o.userOfferId}/show`}>{o.externalId ?? `#${o.userOfferId}`}</Link>
                      {/* „Ocena” z werdyktów API (prototyp, zmiana 15) — powód w dymku. */}
                      <Box sx={{ mt: 0.5 }}>
                        {feasibility ? (
                          <FeasibilityChip feasibility={feasibility} />
                        ) : (
                        <Tooltip
                          title={
                            // Z oceną pary czip nazywa się w dymku „wykonalność”
                            // — liczba pod nim to osobna ocena całej pary.
                            (scoring ? 'Wykonalność (z werdyktów czasu): ' : '') +
                            (graded.reasons.length > 0
                              ? graded.reasons.join(' ')
                              : 'Zdąży na załadunek i dowiezie przed końcem okna rozładunku (szacunek).')
                          }
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            color={GRADE_COLOR[graded.grade]}
                            label={graded.label}
                            // Z klawiatury (prototyp, zmiana 9): fokus pokazuje powód.
                            tabIndex={0}
                            data-testid="suggestion-grade"
                            data-grade={graded.grade}
                          />
                        </Tooltip>
                        )}
                        {/* Ocena pary 0–10 pod czipem (13.10.2026). */}
                        {scoring && <PairScoreLine score={o.pairScore} />}
                        {/* Stan wariantu z autorem (15.10.2026) — jedna linia
                            z wielokropkiem, pełna treść w dymku. */}
                        {detailsOn && o.variants?.own && (
                          <Typography
                            variant="caption"
                            color="primary.main"
                            noWrap
                            data-testid="variant-author"
                            sx={{ display: 'block', maxWidth: VARIANT_LINE_MAX_WIDTH }}
                          >
                            {`wariant: ${o.variants.own.addedBy ?? '—'}`}
                          </Typography>
                        )}
                        {otherVariants && (
                          <Tooltip title={otherVariants}>
                            <Typography
                              variant="caption"
                              color="warning.main"
                              noWrap
                              data-testid="other-variants"
                              sx={{ display: 'block', maxWidth: VARIANT_LINE_MAX_WIDTH }}
                            >
                              {otherVariants}
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {/* Łamiemy między miejscami i między słowami nazwy, nie
                          w środku „DE ZEHDENICK” (`placeText`); strzałka zostaje
                          przy miejscu załadunku. */}
                      <span data-testid="relation-from">{`${placeText(o.from)}\u00a0→`}</span>{' '}
                      <span data-testid="relation-to">{placeText(o.to)}</span>
                      {/* Trasa zlecenia pod relacją (06.10.2026): km ze źródłem,
                          szacunek poglądowy w dymku. */}
                      <OrderRouteNote route={o.route} compact />
                    </TableCell>
                    <TableCell sx={NOWRAP}>
                      <Tooltip title={fullDateTime(o.fromDate)}>
                        <span>{shortDateTime(o.fromDate)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <AccessDistanceCell
                        route={o.accessRoute}
                        legacy={o.accessDistance}
                        showSource={measuredAccess}
                        wrapSource
                      />
                      {/* Koszt pod dolotem, jak w podpowiedzi pojazdów: składnik,
                          nie osobna kolumna do sortowania. */}
                      <AccessCostCell cost={o.accessCost} compact route={o.accessRoute} />
                      {/* Marża i alert marży pod kosztem — ten sam rachunek,
                          jak w podpowiedzi pojazdów; alert, nie ranking. */}
                      <MarginCell margin={o.margin} access={o.accessRoute} route={o.route} wrap />
                    </TableCell>
                    {/* Od kiedy i skąd — ta sama kolumna co przy podpowiedzi
                        pojazdów (omówienie 01.10.2026); wcześniej podpis pod dolotem. */}
                    {freeFromColumn ? (
                      <TableCell>
                        <FreeFromCell position={o.position} />
                      </TableCell>
                    ) : null}
                    {/* Zapas czasu pracy po zleceniu (06.10.2026) — ta sama
                        komórka co w rekomendacjach pojazdów: liczba z dymkiem,
                        jak liczona; brak danych to „—” z powodem. Zapas do okna
                        (15.10.2026) nad nim, w tej samej kolumnie. */}
                    {feasibilityOn ? (
                      <TableCell align="right">
                        <Box data-testid="window-reserve-cell">
                          <WindowReserveCell reserve={o.windowReserve} thresholdH={o.feasibility?.tightThresholdH} />
                        </Box>
                        <Box data-testid="spare-cell">
                          <WorkReserveCell reserve={o.workReserve} />
                        </Box>
                      </TableCell>
                    ) : (
                      <TableCell align="right" data-testid="spare-cell">
                        <WorkReserveCell reserve={o.workReserve} />
                      </TableCell>
                    )}
                    <TableCell>
                      <DeliveryCell delivery={o.delivery} />
                      {detailsOn && <AfterOrderLine afterOrder={o.afterOrder} />}
                    </TableCell>
                    <TableCell>
                      <WarningsList warnings={o.ostrzezenia} />
                    </TableCell>
                    <TableCell align="right">
                      {/* Akcje w pionie (`ACTIONS_COLUMN_SX`): „Wariant” — znacznik,
                          gdy auto już nim jest, inaczej przycisk dodania — a pod
                          nim ikony z podpisem w dymku: podstawy i usunięcie
                          wariantu. */}
                      <Stack
                        direction="column"
                        spacing={0.5}
                        useFlexGap
                        sx={ACTIONS_COLUMN_SX}
                        data-testid="order-actions"
                      >
                        {o.alreadyCandidate ? (
                          <Tooltip
                            title={
                              detailsOn && o.variants?.own?.addedBy
                                ? `${ALREADY_VARIANT_TOOLTIP} Dodał: ${o.variants.own.addedBy}.`
                                : ALREADY_VARIANT_TOOLTIP
                            }
                            describeChild
                            placement="top"
                          >
                            <Chip
                              size="small"
                              color="primary"
                              variant="outlined"
                              label="Wariant"
                              data-testid="variant-chip"
                            />
                          </Tooltip>
                        ) : canManageVariant ? (
                          // Owijka, bo zablokowany przycisk nie pokazałby dymka.
                          // Realizacja pewnie poza oknami (21.10.2026) —
                          // wyszarzony z powodem.
                          <Tooltip title={variantRefusalOf(o) ?? VARIANT_TOOLTIP} describeChild placement="top">
                            <span data-testid="variant-button-hint">
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                aria-label="Dodaj wariant"
                                data-testid="ftl-zlecenie-dodaj-wariant"
                                disabled={busy || variantRefusalOf(o) !== null}
                                sx={NOWRAP}
                                onClick={() => addVariant(o)}
                              >
                                Wariant
                              </Button>
                            </span>
                          </Tooltip>
                        ) : null}
                        <Stack
                          direction="row"
                          spacing={0.5}
                          useFlexGap
                          sx={ACTIONS_ICONS_SX}
                          data-testid="order-icons"
                        >
                          {o.basis && o.basis.length > 0 && (
                            <BasisToggle
                              open={basisFor === o.userOfferId}
                              onToggle={() => setBasisFor((p) => (p === o.userOfferId ? null : o.userOfferId))}
                            />
                          )}
                          {o.alreadyCandidate && canPlan && canManageVariant && (
                            <ReleaseVariantButton
                              userOfferId={o.userOfferId}
                              carId={Number(record.id)}
                              registrationNumber={`${vehicleName} przy zleceniu ${o.externalId ?? `#${o.userOfferId}`}`}
                              label="Usuń wariant"
                              iconOnly
                              onReleased={reload}
                            />
                          )}
                        </Stack>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {basisFor === o.userOfferId && o.basis && (
                    <TableRow>
                      <TableCell colSpan={columns} sx={{ bgcolor: 'action.hover' }}>
                        <BasisList
                          title={`Na jakiej podstawie rekomendujemy ${o.externalId ?? `#${o.userOfferId}`}`}
                          basis={o.basis}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {skipped && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {skipped}
          {/* Lista imienna z powodem (15.10.2026, makieta FTL Etap 1). */}
          {detailsOn && result?.skipped && result.skipped.length > 0 && (
            <>
              {' '}
              <Button
                size="small"
                variant="text"
                onClick={() => setShowSkipped((v) => !v)}
                data-testid="skipped-toggle"
                sx={{ py: 0, minWidth: 0, textTransform: 'none', verticalAlign: 'baseline' }}
              >
                {showSkipped ? 'ukryj listę' : 'pokaż listę z powodami'}
              </Button>
            </>
          )}
        </Typography>
      )}
      {detailsOn && showSkipped && result?.skipped && (
        <SkippedReasonList
          items={result.skipped.map((s) => ({
            key: s.userOfferId,
            label: s.externalId ?? `#${s.userOfferId}`,
            reason: s.reasonText,
          }))}
        />
      )}
      {result && !result.refusal && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {`Lista obejmuje zlecenia bez przypisanego pojazdu z załadunkiem ${withinDays(result.horizonDays)}. `}
          {accessNote
            ? `${accessNote} `
            : 'Dolot to szacunek: odległość w linii prostej × mnożnik terenowy, z dokładnością do ok. ±8%. '}
          {(withTolls
            ? 'Koszt dolotu to szacunek ze stawek spółki pojazdu (paliwo, kierowca, ciągnik) i opłat drogowych ' +
              'wg HERE; stawki w euro '
            : 'Koszt dolotu to szacunek ze stawek spółki pojazdu (paliwo, kierowca, ciągnik); stawki w euro ') +
            'przeliczamy kursem NBP z dnia załadunku zlecenia. Bez stawek albo kursu koszt jest nieznany. '}
          {'Marża to cena zlecenia w złotówkach (waluta obca tym samym kursem) minus koszt dolotu i trasy; ' +
            'to sygnał, kolejność zleceń się nie zmienia. '}
          {accessOriginText(result)}
        </Typography>
      )}
    </SectionCard>
  );
};

/**
 * Stan panelu należy do rekordu — przejście na inny rekord zaczyna od zera
 * (`recordScoped`). Stałe `id` — cel przycisku „Pokaż rekomendowane
 * zlecenia” z banera alertu nad harmonogramem.
 */
const ScopedPanel = recordScoped(CarOrderSuggestionsPanel);
export const CarOrderSuggestions = () => (
  <Box id={ORDER_SUGGESTIONS_ANCHOR_ID} sx={{ scrollMarginTop: 64 }}>
    <ScopedPanel />
  </Box>
);
